import { memo, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import SubscriberStatusBadge from "src/components/blocks/SubscriberStatusBadge.js";
import SubscribeButton from "src/components/blocks/SubscribeButton.js";
import { AuthContext } from "src/utils/AuthContext.js";
import { getPageRoute } from "src/services";
import { trackTileClicked } from "src/utils/sectionAnalytics.js";
import { useRotationOffset } from "src/components/discovery/useRotationOffset.js";
import { formatSubscribers, parseJcalDate, SUBSCRIBER_COUNT_THRESHOLD } from "src/utils/Utils.js";
import { scrollPageCount, scrollPageIndex } from "src/utils/emblaUtils.js";
import {
  eventJcalDates,
  eventSummary,
  nextEventLabel,
  roundRobinPools,
  toSubscribeCalendar,
} from "./calendarTileUtils.js";

/** How long each page of cards holds before auto-advancing. */
const ADVANCE_MS = 7000;

/** Cards kept in rotation — three desktop pages. */
const MAX_CARDS = 9;

/** Countdown label vocabulary, indexed by unit key. */
const UNIT_LABELS = { d: "days", h: "hrs", m: "min", s: "sec" };

/** Live window for events with no DTEND, mirroring the server's current-event rule. */
const NO_DTEND_LIVE_WINDOW_MS = 4 * 60 * 60 * 1000;

/**
 * Round-robin interleave across category pools so adjacent cards differ in
 * kind (sport, then TV, then movie, ...). Each pool keeps only calendars with
 * a live, today, or upcoming featured event — the same rule the card's badge
 * renders by, so no card appears without one — live/today first, then by
 * popularity. (Soonest-first would let always-on filler like nightly news
 * outrank popular shows.) A pool with `preserveOrder` renders as given —
 * for shelves whose stored order IS the curation. Deduped by calendarId
 * across pools.
 *
 * @param {Array<{ key: string, icon?: import("react").ElementType, calendars: Array<object>, preserveOrder?: boolean }>} pools
 * @param {number} nowMs
 * @returns {Array<object>}
 */
export function interleavePools(pools, nowMs) {
  const now = new Date(nowMs);
  const rank = (calendar, liveFirst) => {
    const state = countdownState(calendar.mostRecentEvent, now);
    // Live-first is for pools where being live IS the moment (sports). An
    // always-on daily show would otherwise ride "live now" past genuinely
    // trending titles, so non-liveFirst pools rank purely by trend score.
    const liveish = liveFirst && (state?.live || state?.today) ? 0 : 1;
    // Trending score leads (unscored calendars sit at a neutral midpoint);
    // popularity only breaks ties.
    return [liveish, -(calendar.trending?.score ?? 50), -(calendar.subscribers ?? 0)];
  };
  const ordered = pools.map((pool) => {
    const liveFirst = pool.liveFirst !== false;
    const eligible = (pool.calendars ?? []).filter(
      (calendar) => countdownState(calendar.mostRecentEvent, now) !== null,
    );
    if (!pool.preserveOrder) {
      eligible.sort((a, b) => {
        const [aLive, aTrend, aPop] = rank(a, liveFirst);
        const [bLive, bTrend, bPop] = rank(b, liveFirst);
        return aLive - bLive || aTrend - bTrend || aPop - bPop;
      });
    }
    return eligible.map((calendar) => ({ ...calendar, poolKey: pool.key, poolIcon: pool.icon }));
  });
  // Uncapped: the caller rotates first, then trims to MAX_CARDS — so every
  // pool member gets airtime across visits instead of only the first nine.
  return roundRobinPools(ordered, Number.POSITIVE_INFINITY);
}

/**
 * Rotates the card list left by `offset` (mod length) so a returning visitor
 * opens on a different lead card. Returns the input unchanged for < 2 cards.
 *
 * @param {Array<object>} cards
 * @param {number} offset
 * @returns {Array<object>}
 */
export function rotateCards(cards, offset) {
  if (cards.length < 2) return cards;
  const k = ((offset % cards.length) + cards.length) % cards.length;
  return [...cards.slice(k), ...cards.slice(0, k)];
}

/**
 * The card's one-line subtext. TV and movie cards explain WHY they're
 * trending — the judge's moment phrase ("Season 2 premieres Aug 28") —
 * while sports matchups speak for themselves and keep the event line.
 *
 * @param {{ poolKey?: string, trending?: { moment?: string } }} calendar
 * @param {string|null} eventLine
 * @returns {string|null}
 */
export function heroSubline(calendar, eventLine) {
  if (calendar.poolKey !== "sports" && calendar.trending?.moment) {
    // House style bans em-dashes in customer copy; the middot matches the
    // event line's separator.
    return calendar.trending.moment.replace(/\s*[—–]\s*/g, " · ");
  }
  return eventLine;
}

/**
 * The card's countdown state at `now`: live badge, "Today" (all-day event
 * today), segmented countdown units, or nothing.
 *
 * @param {string|null} mostRecentEvent
 * @param {Date} now
 * @returns {{ live?: boolean, today?: boolean, units?: Array<[number, string]> }|null}
 */
export function countdownState(mostRecentEvent, now) {
  const { dtstart, dtend, hasEnd } = eventJcalDates(mostRecentEvent);
  if (!dtstart?.[3]) return null;
  const { eventParsed: start, isAllDayEvent } = parseJcalDate(dtstart);

  if (isAllDayEvent) {
    if (now.toDateString() === start.toDateString()) return { today: true };
  } else {
    // DTEND-less feeds still count as live inside the same window the server
    // uses to feature an in-progress event.
    const end = hasEnd && dtend?.[3]
      ? parseJcalDate(dtend).eventParsed
      : new Date(start.getTime() + NO_DTEND_LIVE_WINDOW_MS);
    if (now >= start && now <= end) return { live: true };
  }

  const delta = start.getTime() - now.getTime();
  if (delta <= 0) return null;
  return { units: countdownUnits(delta) };
}

/**
 * Segment tuples for a positive countdown. Under a day it's always
 * `[[h,"h"],[m,"m"],[s,"s"]]`; a day or more shows `[[d,"d"],[h,"h"],[m,"m"]]`,
 * or appends seconds when `withSeconds` is set.
 *
 * @param {number} deltaMs milliseconds until the target (assumed > 0)
 * @param {{ withSeconds?: boolean }} [opts]
 * @returns {Array<[number, string]>}
 */
export function countdownUnits(deltaMs, { withSeconds = false } = {}) {
  const total = Math.floor(deltaMs / 1000);
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (d <= 0) return [[h, "h"], [m, "m"], [s, "s"]];
  return withSeconds ? [[d, "d"], [h, "h"], [m, "m"], [s, "s"]] : [[d, "d"], [h, "h"], [m, "m"]];
}

/** Pulsing "Live now" badge shown in place of the countdown. */
const LiveBadge = ({ label = "Live now" }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "7px",
      width: "max-content",
      fontSize: "14px",
      fontWeight: 800,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: "secondary.main",
      // Solid translucent fill: a backdrop blur here would force an offscreen
      // GPU surface per badge.
      backgroundColor: "rgba(150,35,40,0.6)",
      border: "1px solid rgba(235,87,87,0.55)",
      borderRadius: "999px",
      px: 1.5,
      py: 0.5,
      "@keyframes livePulse": { "50%": { opacity: 0.35 } },
    }}
  >
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: "delete.main",
        animation: "livePulse 1.5s infinite",
        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
      }}
    />
    {label}
  </Box>
);

/** Segment dimensions by size — "sm" (card ticker, default) and "lg" (page hero). */
const COUNTDOWN_SIZES = {
  sm: { gap: "6px", minWidth: 56, px: 1, pt: "6px", pb: "5px", radius: "11px", value: "20px", label: "14px" },
  lg: { gap: { xs: "6px", md: "8px" }, minWidth: { xs: 58, md: 70 }, px: { xs: 1, md: 1.25 }, pt: "8px", pb: "7px", radius: "12px", value: { xs: "22px", md: "30px" }, label: { xs: "12px", md: "13px" } },
};

/** Frosted-glass countdown segments (DD/HH/MM or HH/MM/SS). */
export const CountdownSegments = ({ units, size = "sm" }) => {
  const s = COUNTDOWN_SIZES[size] ?? COUNTDOWN_SIZES.sm;
  return (
  <Box sx={{ display: "flex", gap: s.gap, width: "max-content" }}>
    {units.map(([value, unit]) => (
      <Box
        key={unit}
        sx={{
          minWidth: s.minWidth,
          textAlign: "center",
          px: s.px,
          pt: s.pt,
          pb: s.pb,
          borderRadius: s.radius,
          // Solid translucent scrim: a backdrop blur here would force an
          // offscreen GPU surface per segment, three per card.
          backgroundColor: "rgba(18,18,22,0.55)",
          border: "1px solid rgba(255,255,255,0.22)",
        }}
      >
        <Typography
          component="b"
          sx={{
            display: "block",
            fontSize: s.value,
            fontWeight: 800,
            lineHeight: 1,
            fontVariantNumeric: "tabular-nums",
            color: "secondary.main",
          }}
        >
          {String(value).padStart(2, "0")}
        </Typography>
        <Typography
          component="span"
          sx={{
            display: "block",
            fontSize: s.label,
            fontWeight: 700,
            letterSpacing: "0.06em",
            mt: "3px",
            color: "overlay.white60",
            textTransform: "uppercase",
          }}
        >
          {UNIT_LABELS[unit]}
        </Typography>
      </Box>
    ))}
  </Box>
  );
};

/**
 * Self-ticking countdown badge for one card. Owning the ticker here scopes
 * the per-second re-render to these few spans instead of the whole hero —
 * every second only while live or showing seconds, once a minute otherwise.
 *
 * @param {{ mostRecentEvent: string|null }} props
 */
const HeroCountdown = memo(({ mostRecentEvent }) => {
  const [now, setNow] = useState(() => new Date());
  const state = countdownState(mostRecentEvent, now);

  const fast = Boolean(state?.live || state?.units?.some(([, unit]) => unit === "s"));
  useEffect(() => {
    if (!state) return undefined;
    const timer = setInterval(() => setNow(new Date()), fast ? 1000 : 60000);
    return () => clearInterval(timer);
  }, [fast, !state]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state?.live) return <LiveBadge />;
  if (state?.today) return <LiveBadge label="Today" />;
  if (state?.units) return <CountdownSegments units={state.units} />;
  return null;
});
HeroCountdown.displayName = "HeroCountdown";

/**
 * One hero card: art background, live badge or glass countdown, name,
 * subscriber count / status pill, and the standard Add to Calendar button.
 *
 * @param {{ calendar: object, section?: object }} props
 */
const HeroCard = memo(({ calendar, section }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // The actual event ("Steelers at Ravens"), shown only when it says more
  // than the calendar name already does. Leading emoji markers are stripped,
  // and all-day events (movie releases) show the date without a time.
  const eventLine = useMemo(() => {
    const summary = eventSummary(calendar.mostRecentEvent)?.replace(/^[^\p{L}\p{N}]+/u, "");
    if (!summary || summary.trim().toLowerCase() === (calendar.name ?? "").trim().toLowerCase()) {
      return null;
    }
    const { dtstart } = eventJcalDates(calendar.mostRecentEvent);
    const { eventParsed, isAllDayEvent } = parseJcalDate(dtstart);
    const when = isAllDayEvent
      ? eventParsed?.toLocaleDateString("en-US", { month: "short", day: "numeric" })
      : nextEventLabel(calendar.mostRecentEvent);
    return when ? `${summary} · ${when}` : summary;
  }, [calendar.mostRecentEvent, calendar.name]);

  const subline = heroSubline(calendar, eventLine);

  // `SubscribeButton` reads the Calendar-document shape.
  const buttonCalendar = useMemo(
    () => toSubscribeCalendar(calendar),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [calendar.calendarId, calendar.handle, calendar.name, calendar.picture, calendar.stanzaLink],
  );

  // Meta priority: real subscriber count > genre tags > status pill.
  const genres = (calendar.subscribers ?? 0) <= SUBSCRIBER_COUNT_THRESHOLD
    ? (calendar.genres ?? []).slice(0, 2)
    : [];
  const meta = useMemo(
    () =>
      (calendar.subscribers ?? 0) > SUBSCRIBER_COUNT_THRESHOLD
        ? formatSubscribers(calendar.subscribers, { includeSubscriberText: true })
        : formatSubscribers(calendar.subscribers, {
            includeSubscriberText: true,
            showAlternativeText: true,
            createdAt: calendar.createdAt,
            stableKey: calendar.handle,
          }),
    [calendar.subscribers, calendar.createdAt, calendar.handle],
  );

  const open = () => {
    trackTileClicked({ user, section, calendar });
    navigate(
      getPageRoute("calendar", "CALENDAR_EVENTS", {
        handle: calendar.handle,
        calendarId: calendar.calendarId,
      }),
    );
  };

  return (
    <Box
      role="link"
      tabIndex={0}
      onClick={open}
      onKeyDown={(event) => (event.key === "Enter" || event.key === " ") && open()}
      sx={{
        position: "relative",
        boxSizing: "border-box",
        borderRadius: "16px",
        overflow: "hidden",
        cursor: "pointer",
        p: 2.25,
        minHeight: { xs: 210, md: 306 },
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        color: "secondary.main",
        backgroundImage: calendar.picture
          ? `linear-gradient(180deg, rgba(10,10,11,0.08) 0%, rgba(10,10,11,0.55) 55%, rgba(10,10,11,0.9) 100%), url(${calendar.picture})`
          : "linear-gradient(120deg, #141c24 0%, #333339 100%)",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <HeroCountdown mostRecentEvent={calendar.mostRecentEvent} />
      <Typography
        component="h3"
        sx={{
          fontSize: { xs: "1.1rem", md: "1.56rem" },
          fontWeight: 800,
          lineHeight: 1.15,
          mt: 1.25,
          color: "inherit",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}
      >
        {calendar.poolIcon && (
          <calendar.poolIcon sx={{ fontSize: "1.1em", verticalAlign: "-0.15em", mr: 0.75 }} />
        )}
        {calendar.name}
      </Typography>
      {subline && (
        <Typography
          variant="body2"
          data-testid="hero-subline"
          sx={{
            color: "overlay.white70",
            mt: 0.25,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {subline}
        </Typography>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mt: 1.25,
          flexWrap: "wrap",
          // The standard button is black-on-white; invert it here so it
          // stays visible over the dark art scrim.
          "& .MuiButton-contained": {
            backgroundColor: "transparent",
            border: "1px solid",
            borderColor: "overlay.white70",
            color: "secondary.main",
            "&:hover": { backgroundColor: "overlay.white10", borderColor: "secondary.main" },
          },
          // The added state's label and link/refresh/remove icons are dark by
          // default; lift them to white over the art too.
          "& .MuiButton-text.Mui-disabled": { color: "secondary.main", opacity: 0.85 },
          "& .MuiIconButton-root .material-symbols-outlined": { color: "secondary.main" },
        }}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <SubscribeButton
          user={user}
          calendar={buttonCalendar}
          handle={calendar.handle}
          isEventCard={true}
        />
        {genres.length > 0 ? (
          genres.map((genre) => (
            <Box
              key={genre}
              sx={{
                fontSize: "14px",
                fontWeight: 700,
                color: "secondary.main",
                backgroundColor: "rgba(18,18,22,0.5)",
                border: "1px solid rgba(255,255,255,0.22)",
                borderRadius: "999px",
                px: 1.25,
                py: 0.5,
                textTransform: "capitalize",
              }}
            >
              {genre.replaceAll("-", " ")}
            </Box>
          ))
        ) : (
          meta && (
            <SubscriberStatusBadge variant="caption" sx={{ color: "overlay.white70" }}>
              {meta}
            </SubscriberStatusBadge>
          )
        )}
      </Box>
    </Box>
  );
});
HeroCard.displayName = "HeroCard";

/**
 * "Live today" hero row: three cards per desktop page (one per swipe on
 * mobile), interleaving category pools so sports, TV and movies all surface.
 * Auto-advances by page; pauses on hover/touch and under reduced motion.
 *
 * @param {{ title?: string, titleIcon?: import("react").ReactNode, pools: Array<{ key: string, calendars: Array<object> }>, section?: object }} props
 */
const LiveTodayHero = ({ title, titleIcon, pools = [], section }) => {
  const scrollerRef = useRef(null);
  const [page, setPage] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const pausedRef = useRef(false);

  // Opens a returning signed-out visitor on a different hero card each visit —
  // matters most on mobile, where only the first card is visible. Keyed per
  // hero (by section slug) so the home page's heroes rotate independently
  // instead of in lockstep off a single shared offset.
  const rotationOffset = useRotationOffset(`liveTodayHeroOffset:${section?.slug ?? "default"}`);

  const cards = useMemo(
    () => rotateCards(interleavePools(pools, Date.now()), rotationOffset).slice(0, MAX_CARDS),
    [pools, rotationOffset],
  );

  const reducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    const measure = () => setPageCount(scrollPageCount(el));
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [cards.length]);

  useEffect(() => {
    if (reducedMotion || pageCount < 2) return undefined;
    const timer = setInterval(() => {
      const el = scrollerRef.current;
      if (!el || pausedRef.current) return;
      const next = scrollPageIndex(el) + 1;
      el.scrollTo({ left: next >= pageCount ? 0 : next * el.clientWidth, behavior: "smooth" });
    }, ADVANCE_MS);
    return () => clearInterval(timer);
  }, [reducedMotion, pageCount]);

  if (!cards.length) return null;

  return (
    <Box
      data-testid="live-today-hero"
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { pausedRef.current = false; }}
      onTouchStart={() => { pausedRef.current = true; }}
      sx={{ mb: 7 }}
    >
      {title && (
        <Typography
          variant="sectionTitle"
          component="h2"
          sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
        >
          {titleIcon}
          {title}
        </Typography>
      )}
      <Box
        ref={scrollerRef}
        onScroll={(event) => {
          const el = event.currentTarget;
          setPage(scrollPageIndex(el));
        }}
        sx={{
          display: "flex",
          gap: 2,
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          overscrollBehaviorX: "none",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          "& > *": {
            flex: { xs: "0 0 86%", md: "0 0 calc((100% - 32px) / 3)" },
            minWidth: 0,
            scrollSnapAlign: "start",
          },
        }}
      >
        {cards.map((calendar) => (
          <HeroCard
            key={calendar.calendarId ?? calendar.handle}
            calendar={calendar}
            section={section}
          />
        ))}
      </Box>

      {pageCount > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 0.75, mt: 1.5 }}>
          {Array.from({ length: pageCount }, (_, i) => (
            <Box
              key={i}
              onClick={() => {
                const el = scrollerRef.current;
                el?.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
              }}
              sx={{
                height: 6,
                width: i === page ? 17 : 6,
                borderRadius: 3,
                cursor: "pointer",
                backgroundColor: i === page ? "text.primary" : "overlay.black15",
                transition: "all 0.2s ease",
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default LiveTodayHero;
